import { Component, h, JSX } from '@stencil/core';
import { HostElement, Element, State, Prop, Host } from '@stencil/core/internal';

@Component({
    tag: 'web-visibility-trigger',
    styleUrl: 'visibility-trigger.scss',
    shadow: false
})
export class VisibilityTrigger {
    @Element() hostElement: HostElement;

    /**
     * Whether the element is intersecting with the target element.
     */
    @State() intersecting = true;

    /**
     * The id of the target element to observe.
     */
    @Prop() targetElementId = 'trigger-element';

    /**
     * The threshold to trigger the visibility.
     */
    @Prop() threshold? = 0;

    /**
     * Determines when the trigger should activate based on element position.
     * 'before': Trigger when element is above viewport
     * 'after': Trigger when element is below viewport
     * 'both': Trigger when element is outside viewport in any direction
     */
    @Prop() triggerPosition?: 'before' | 'after' | 'both' = 'both';

    private observer: IntersectionObserver;
    private targetElement: HTMLElement;

    componentDidLoad(): void {
        this.setupObserver();
    }

    disconnectedCallback(): void {
        this.cleanupObserver();
    }

    private setupObserver(): void {
        this.cleanupObserver();
        
        this.targetElement = document.querySelector(`#${this.targetElementId}`);
        
        if (this.targetElement) {
            this.createIntersectionObserver();
            this.observer.observe(this.targetElement);
        }
    }

    private createIntersectionObserver(): void {
        this.observer = new IntersectionObserver(
            entries => this.handleIntersection(entries[0]),
            {
                threshold: this.threshold,
                rootMargin: '0px'
            }
        );
    }

    private handleIntersection(entry: IntersectionObserverEntry): void {
        const { isIntersecting, boundingClientRect, rootBounds } = entry;
        
        if (!rootBounds) {
            this.intersecting = true;
            return;
        }

        switch (this.triggerPosition) {
            case 'before':
                this.intersecting = boundingClientRect.bottom > rootBounds.top;
                break;
            case 'after':
                this.intersecting = boundingClientRect.top > rootBounds.top;
                break;
            default:
                this.intersecting = isIntersecting;
                break;
        }
    }

    private cleanupObserver(): void {
        if (this.observer && this.targetElement) {
            this.observer.unobserve(this.targetElement);
            this.observer.disconnect();
            this.observer = null;
        }
    }

    public render(): JSX.Element {
        const visibilityClass = this.getVisibilityClass();
        
        return (
            <Host class={visibilityClass}>
                <slot />
            </Host>
        );
    }

    private getVisibilityClass(): string {
        return `trigger--${this.intersecting ? 'hidden' : 'visible'}`;
    }
}